import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { setupService } from "../../../Core/Api/services";
import {
  AssetDeliveryPerMonthDto,
  DataSizeDeliveredPerDayModel,
  DataSizeDeliveredPerMonthDto,
  IngestedAssetsPerMonthDto,
  QualifiedAssetsPerDayDto,
  QualifiedAssetsPerMonthDataModel,
  QualifiedAssetsPerMonthDto,
} from "../../../Core/Api/Types/db-graphset-cache-infos";

import "../../../Theme/CSS/reporting-metrics/reporting-metrics.css";
import TableSkeleton from "../../../Components/Table/TableSkeleton";
import { useTranslation } from "react-i18next";

function ReportingMetrics() {
  const { t } = useTranslation();
  const theme = useTheme();

  const [dataSizeDeliveredDataModel, setDataSizeDeliveredDataModel] = useState<DataSizeDeliveredPerMonthDto>();
  const [ingestedAssetsPerMonthDto, setIngestedAssetsPerMonthDto] = useState<IngestedAssetsPerMonthDto | undefined>();
  const [assetDeliveryPerMonthDto, setAssetDeliveryPerMonthDto] = useState<AssetDeliveryPerMonthDto | undefined>();
  const [qualifiedAssetsPerMonth, setQualifiedAssetsPerMonth] = useState<QualifiedAssetsPerMonthDto>();
  const [selectedDayQADto, setSelectedDayQADto] = useState<QualifiedAssetsPerDayDto | undefined>();
  const [selectedDayDSDto, setSelectedDayDSDto] = useState<DataSizeDeliveredPerDayModel[] | undefined>();

  useEffect(() => {
    setupService.GetGraphDataSizeDeliveredData().then((response) => {
      setDataSizeDeliveredDataModel(response);
    });
    setupService.GetGraphDataIngestedAssetsPerMonth().then((response) => {
      setIngestedAssetsPerMonthDto(response);
    });
    setupService.GetGrapDeliveredPerMonth().then((response) => {
      setAssetDeliveryPerMonthDto(response);
    });
    setupService.GetQualifiedAssetsPerMonth().then((response) => {
      setQualifiedAssetsPerMonth(response);
    });
  }, []);

  const renderLabel = (props, dataAccessor: any) => {
    const { x, y, width, index } = props;
    if (dataAccessor.data[index] === undefined) return;
    if (!(y >= 0 && x >= 0 && width >= 0)) return;
    const displayValue = dataAccessor
      ? dataAccessor.keys.reduce(
          (acc, x) => (dataAccessor.data[index][x.key] ? acc + dataAccessor.data[index][x.key] : acc),
          0
        )
      : 0;

    if (displayValue === 0) return;

    return generateLabel(x, y, width, displayValue);
  };

  const generateLabel = (x: number, y: number, width: number, value: number) => (
    <g>
      <filter id="blurMe">
        <feGaussianBlur stdDeviation="2" />
      </filter>
      <rect
        fill={theme.palette.mode === "dark" ? "black" : "white"}
        opacity={0.08}
        filter="url(#blurMe)"
        x={x + width / 4}
        y={y + 3}
        width={width / 2}
        height={18}
      />
      <text
        x={x + width / 2}
        y={y + 12}
        fontSize={14}
        fontWeight={"bold"}
        fill={theme.palette.mode === "dark" ? "white" : "black"}
        textAnchor="middle"
        dominantBaseline="middle"
      >
        {value}
      </text>
    </g>
  );

  return (
    <Stack spacing={2} pl={2}>
      {dataSizeDeliveredDataModel && dataSizeDeliveredDataModel.months.length && !selectedDayDSDto ? (
        <Stack spacing={1} flex={1}>
          <Typography variant="h5" p={2}>
            {t("page:body.dataSizeGenerated")}
          </Typography>
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={dataSizeDeliveredDataModel.months}>
              <XAxis type="category" dataKey="month" />
              <YAxis type="number" dataKey="value" domain={["auto", "auto"]} unit="TB" allowDecimals />
              <Tooltip contentStyle={{ color: theme.palette.grey[800] }} />
              <Legend />
              <CartesianGrid strokeDasharray="5" vertical={false} />
              <Line
                name={t("page:body.dataSizeGenerated")}
                type="monotone"
                dataKey="value"
                stroke="#8884d8"
                points={[]}
                label={(props) =>
                  generateLabel(
                    props.x,
                    props.y,
                    5,
                    Math.round(dataSizeDeliveredDataModel.months[props.index].value * 100) / 100
                  )
                }
                activeDot={{
                  onClick: (_, event) => {
                    setSelectedDayDSDto(event["payload"].days as DataSizeDeliveredPerDayModel[] | undefined);
                  },
                }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Stack>
      ) : (
        <TableSkeleton />
      )}
      {selectedDayDSDto && (
        <Stack spacing={1} flex={1}>
          <Typography variant="h5" p={2}>
            {t("page:body.dataSizeGeneratedByMonth")}
          </Typography>
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={selectedDayDSDto}>
              <XAxis type="category" dataKey="name" />
              <YAxis type="number" dataKey="value" domain={["auto", "auto"]} unit="TB" allowDecimals />
              <Tooltip contentStyle={{ color: theme.palette.grey[800] }} />
              <Legend />
              <CartesianGrid strokeDasharray="5" vertical={false} />
              <Line
                name={t("page:body.dataSizeGenerated")}
                type="monotone"
                dataKey="value"
                stroke="#8884d8"
                points={[]}
              />
            </LineChart>
          </ResponsiveContainer>
          <Stack direction="row" justifyContent={"space-between"}>
            <Box></Box>
            <Button variant="outlined" color="primary" size="small" onClick={() => setSelectedDayDSDto(undefined)}>
              {"< "}
              {t("button:backToMonths")}
            </Button>
          </Stack>
        </Stack>
      )}
      {ingestedAssetsPerMonthDto ? (
        <Stack spacing={1} flex={1}>
          <Typography variant="h5" p={2}>
            {t("common:ingests")}
          </Typography>
          <ResponsiveContainer height={400} width="100%">
            <BarChart data={ingestedAssetsPerMonthDto.data} margin={{ top: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="category" dataKey="month" />
              <YAxis />
              <Tooltip contentStyle={{ color: theme.palette.grey[800] }} />
              <Legend verticalAlign="bottom" />
              {ingestedAssetsPerMonthDto.keys.map((key, index) => (
                <Bar key={index} dataKey={key.key} stackId="a" fill={key.color}>
                  {" "}
                  {index === ingestedAssetsPerMonthDto.keys.length - 1 && (
                    <LabelList dataKey="name" content={(props) => renderLabel(props, ingestedAssetsPerMonthDto)} />
                  )}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Stack>
      ) : (
        <TableSkeleton />
      )}
      {assetDeliveryPerMonthDto ? (
        <Stack spacing={1} flex={1}>
          <Typography variant="h5" p={2}>
            {t("common:orders")}
          </Typography>
          <ResponsiveContainer height={400} width="100%">
            <BarChart data={assetDeliveryPerMonthDto.data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="category" dataKey="month" />
              <YAxis />
              <Tooltip contentStyle={{ color: theme.palette.grey[800] }} />
              <Legend />
              {assetDeliveryPerMonthDto.keys.map((key, index) => (
                <Bar key={index} dataKey={key.key} stackId="a" fill={key.color}>
                  {index === assetDeliveryPerMonthDto.keys.length - 1 && (
                    <LabelList dataKey="name" content={(props) => renderLabel(props, assetDeliveryPerMonthDto)} />
                  )}{" "}
                </Bar>
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Stack>
      ) : (
        <TableSkeleton />
      )}
      {qualifiedAssetsPerMonth && qualifiedAssetsPerMonth.months.length && !selectedDayQADto ? (
        <Stack spacing={1} flex={1}>
          <Typography variant="h5" p={2}>
            {t("page:body.qualifications")}
          </Typography>
          <ResponsiveContainer height={400} width="100%">
            <BarChart data={qualifiedAssetsPerMonth.months}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="category" dataKey="month" />
              <YAxis />
              <Tooltip contentStyle={{ color: theme.palette.grey[800] }} />
              <Legend />
              <Bar
                dataKey={"sum"}
                fill={"#ffc107"}
                onClick={(data) => {
                  setSelectedDayQADto((data as QualifiedAssetsPerMonthDataModel).dayDto);
                }}
              >
                <LabelList
                  dataKey="name"
                  content={(props: any) =>
                    generateLabel(
                      props.x,
                      props.y,
                      props.width,
                      qualifiedAssetsPerMonth.months[props.index as number].sum
                    )
                  }
                />{" "}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Stack>
      ) : (
        <TableSkeleton />
      )}
      {selectedDayQADto && (
        <Stack spacing={1} flex={1}>
          <>
            <Typography variant="h5" p={2}>
              {t("page:body.qualifiedAssetsPerDay")}
            </Typography>
            <ResponsiveContainer height={400} width="100%">
              <BarChart data={selectedDayQADto.data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="category" dataKey="name" />
                <YAxis />
                <Tooltip contentStyle={{ color: theme.palette.grey[800] }} />
                <Legend />
                {selectedDayQADto.keys.map((key, index) => (
                  <Bar key={index} dataKey={key.key} stackId="a" fill={key.color}>
                    {index === selectedDayQADto.keys.length - 1 && (
                      <LabelList dataKey="name" content={(props) => renderLabel(props, selectedDayQADto)} />
                    )}
                  </Bar>
                ))}
              </BarChart>
            </ResponsiveContainer>
            <Stack direction="row" justifyContent={"space-between"}>
              <Box></Box>
              <Button variant="outlined" color="primary" size="small" onClick={() => setSelectedDayQADto(undefined)}>
                {"< "}
                {t("button:backToMonths")}
              </Button>
            </Stack>
          </>
        </Stack>
      )}
    </Stack>
  );
}

export default ReportingMetrics;
